import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Download,
  Info,
  Loader2,
  Play,
  RotateCcw,
  Rocket,
  Search,
  XCircle,
} from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { OPEN_OS_DETAILS_KEY } from '../../pg-native/useOpenOsDetailsFromNav';
import './PainelControleDbVervifyCutover.css';

const API_BASE_URL = getApiBaseUrl();
const POLL_MS = 1500;

async function api(path, options = {}) {
  const authToken = localStorage.getItem('authToken');
  const response = await fetch(`${API_BASE_URL}/api/config/db-vervify${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.success === false) throw new Error(result.message || 'Falha na comunicação com o servidor');
  return result;
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

const STAGE_LABELS = { open: 'Em aberto', technician: 'Com técnico', closing: 'Aguardando fechamento' };

function phaseInfo(status, progress) {
  const phase = progress?.phase || status?.phase || 'none';
  if (phase === 'drained') {
    return { tone: 'is-done', title: 'Operação 100% Postgres', desc: `Desde ${formatDateTime(progress?.drainedAt || status?.cutover?.erp_drained_at)}.` };
  }
  if (phase === 'transition') {
    const n = progress?.pending ?? null;
    return {
      tone: 'is-active',
      title: n == null ? 'Em transição' : `Em transição — ${n} OS do ERP em aberto`,
      desc: `Operação nativa ativa desde ${formatDateTime(status?.cutover?.cutover_at)}. As OS do ERP abertas até essa data terminam no fluxo atual.`,
    };
  }
  if (phase === 'scheduled') {
    return { tone: 'is-scheduled', title: 'Ativação agendada', desc: `Prevista para ${formatDateTime(status?.cutover?.cutover_at)}.` };
  }
  return { tone: '', title: 'Não ativado', desc: 'Técnicos e OS continuam no fluxo atual (SQL Server).' };
}

function toCsv(items) {
  const head = ['OS', 'Cliente', 'Tipo', 'Status', 'Técnico', 'Abertura', 'Dias em aberto', 'Coluna'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = items.map((i) => [i.os, i.customer, i.type, i.status, i.technician, formatDate(i.openedAt), i.daysOpen, STAGE_LABELS[i.stage]]);
  return [head, ...rows].map((r) => r.map(esc).join(';')).join('\r\n');
}

function minScheduleValue() {
  const d = new Date(Date.now() + 5 * 60_000);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function PainelControleDbVervifyCutover({ toastSuccess, toastError, reloadKey }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const [filter, setFilter] = useState('');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const st = await api('/cutover');
      setStatus(st);
      if (st.eligible && (st.phase === 'transition' || st.phase === 'drained')) {
        setProgress(await api('/cutover/progress').catch(() => null));
      } else {
        setProgress(null);
      }
    } catch (e) {
      if (!silent) toastError?.(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const running = status?.job?.status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(t);
  }, [running, load]);

  const run = async (action, body = {}) => {
    setBusy(action);
    try {
      await api('/cutover', { method: 'POST', body: JSON.stringify({ action, ...body }) });
      if (action === 'rollback') toastSuccess?.('Cutover revertido');
      if (action === 'schedule') toastSuccess?.('Ativação agendada');
      setConfirm('');
      await load({ silent: true });
    } catch (e) {
      toastError?.(e.message);
    } finally {
      setBusy('');
    }
  };

  const items = useMemo(() => progress?.items || [], [progress]);
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => [i.os, i.customer, i.type, i.status, i.technician].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [items, filter]);

  const exportCsv = () => {
    const blob = new Blob([`\ufeff${toCsv(filtered)}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'os-erp-pendentes.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openOnBoard = (os) => {
    sessionStorage.setItem(OPEN_OS_DETAILS_KEY, os);
    navigate('/');
  };

  const openClosing = async (os) => {
    try {
      await navigator.clipboard.writeText(os);
      toastSuccess?.(`OS ${os} copiada para buscar no fechamento`);
    } catch {
      /* clipboard indisponível: só navega */
    }
    navigate('/fechamento');
  };

  if (loading) {
    return <p className="pc-dbv-hint"><Loader2 size={13} className="pcs2-spin" aria-hidden /> Carregando operação nativa…</p>;
  }
  if (!status?.eligible) {
    return <p className="pc-dbv-hint">Não elegível: este cliente não possui banco de dados Vervify configurado.</p>;
  }

  const phase = progress?.phase || status.phase;
  const info = phaseInfo(status, progress);
  const ready = status.nativeOperationReady;
  const checklistReady = status.checklist?.ready;
  const canActivate = ready && checklistReady && !running && (phase === 'none' || phase === 'scheduled');
  const lockedTitle = !ready
    ? 'Liberado quando todas as etapas da operação nativa estiverem concluídas. Até lá, os testes usam o script pg-cutover.'
    : !checklistReady
      ? 'Conclua os itens pendentes do checklist.'
      : undefined;
  const openAtCutover = Number(progress?.openAtCutover || 0);
  const pending = Number(progress?.pending || 0);
  const pct = openAtCutover > 0 ? Math.round(((openAtCutover - Math.min(pending, openAtCutover)) / openAtCutover) * 100) : 100;

  return (
    <div className="pc-dbv-cut">
      <div className={`pc-dbv-cut-phase ${info.tone}`}>
        <span className="pc-dbv-cut-phase-dot" aria-hidden />
        <div>
          <span className="pc-dbv-cut-phase-title">{info.title}</span>
          <span className="pc-dbv-cut-phase-desc">{info.desc}</span>
        </div>
      </div>

      {phase === 'none' || phase === 'scheduled' ? (
        <>
          <ul className="pc-dbv-cut-check">
            {status.checklist.items.map((i) => (
              <li key={i.key} className={!i.available ? 'is-future' : i.ok ? 'is-ok' : 'is-missing'}>
                {!i.available ? (
                  <Circle size={14} strokeWidth={1.75} aria-hidden />
                ) : i.ok ? (
                  <CheckCircle2 size={14} strokeWidth={2} aria-hidden />
                ) : (
                  <XCircle size={14} strokeWidth={2} aria-hidden />
                )}
                <span>{i.label}</span>
                {!i.available ? <em>disponível na Etapa {i.stage}</em> : null}
                {i.available && !i.ok && i.key === 'migrated' ? <em>execute a migração acima</em> : null}
              </li>
            ))}
          </ul>

          <div className="pc-dbv-cut-actions">
            <input
              type="datetime-local"
              className="customers-oauth-input pc-dbv-cut-datetime"
              value={scheduleAt}
              min={minScheduleValue()}
              onChange={(e) => setScheduleAt(e.target.value)}
              disabled={!canActivate || Boolean(busy)}
              aria-label="Data e hora do cutover"
              title={lockedTitle}
            />
            <button
              type="button"
              className="pc-dbv-btn"
              onClick={() => run('schedule', { at: new Date(scheduleAt).toISOString() })}
              disabled={!canActivate || !scheduleAt || Boolean(busy)}
              title={lockedTitle}
            >
              {busy === 'schedule' ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : <ClipboardCheck size={14} aria-hidden />}
              Agendar
            </button>
            {confirm === 'activate' ? (
              <>
                <button type="button" className="pc-dbv-btn" onClick={() => setConfirm('')} disabled={Boolean(busy)}>Cancelar</button>
                <button type="button" className="pc-dbv-btn is-primary" onClick={() => run('activate')} disabled={Boolean(busy)}>
                  {busy === 'activate' ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : <Play size={14} aria-hidden />}
                  Confirmar ativação
                </button>
              </>
            ) : (
              <button type="button" className="pc-dbv-btn is-primary" onClick={() => setConfirm('activate')} disabled={!canActivate || Boolean(busy)} title={lockedTitle}>
                <Rocket size={14} aria-hidden />
                {phase === 'scheduled' ? 'Iniciar agora' : 'Ativar operação nativa'}
              </button>
            )}
            {phase === 'scheduled' ? (
              <button type="button" className="pc-dbv-btn" onClick={() => run('rollback')} disabled={Boolean(busy) || running}>
                <RotateCcw size={14} aria-hidden /> Cancelar agendamento
              </button>
            ) : null}
          </div>
          {confirm === 'activate' ? (
            <p className="pc-dbv-confirm-text">
              A partir da ativação, técnicos e OS novas passam a ser do banco de dados Vervify. As OS do ERP já abertas terminam no fluxo atual.
              Antes de ativar, é feita a cópia de segurança do banco Vervify e das tabelas do Pilot no SQL Server; sem ela, a ativação não conclui.
            </p>
          ) : null}
        </>
      ) : (
        <div className="pc-dbv-cut-actions">
          {confirm === 'rollback' ? (
            <>
              <button type="button" className="pc-dbv-btn" onClick={() => setConfirm('')} disabled={Boolean(busy)}>Cancelar</button>
              <button type="button" className="pc-dbv-btn is-primary" onClick={() => run('rollback')} disabled={Boolean(busy)}>
                {busy === 'rollback' ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : <RotateCcw size={14} aria-hidden />}
                Confirmar reversão
              </button>
            </>
          ) : (
            <button type="button" className="pc-dbv-btn" onClick={() => setConfirm('rollback')} disabled={Boolean(busy) || running} title="Só é permitido enquanto não houver registro nativo.">
              <RotateCcw size={14} aria-hidden /> Reverter cutover
            </button>
          )}
        </div>
      )}

      {status.job ? (
        <div className="pc-dbv-run">
          <div className="pc-dbv-run-head">
            <span className="pc-dbv-run-title">
              {running ? 'Ativando operação nativa…' : status.job.status === 'success' ? 'Ativação concluída' : 'Ativação falhou'}
            </span>
          </div>
          <ol className="pc-dbv-steps">
            {status.job.steps.map((s) => (
              <li key={s.key} className={`pc-dbv-step is-${s.status}`}>
                {s.status === 'done' ? (
                  <CheckCircle2 size={15} strokeWidth={2} className="pc-dbv-step-ico is-done" aria-hidden />
                ) : s.status === 'failed' ? (
                  <XCircle size={15} strokeWidth={2} className="pc-dbv-step-ico is-failed" aria-hidden />
                ) : s.status === 'running' ? (
                  <Loader2 size={15} strokeWidth={2} className="pc-dbv-step-ico is-running" aria-hidden />
                ) : (
                  <Circle size={15} strokeWidth={1.75} className="pc-dbv-step-ico" aria-hidden />
                )}
                <span className="pc-dbv-step-label">{s.label}</span>
                <span />
                {s.error ? <span className="pc-dbv-step-error">{s.error}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {phase === 'transition' ? (
        <div className="pc-dbv-run">
          <div className="pc-dbv-run-head">
            <span className="pc-dbv-run-title">
              {openAtCutover > 0 ? `${openAtCutover - Math.min(pending, openAtCutover)} de ${openAtCutover} OS do ERP encerradas` : 'OS do ERP pendentes'}
            </span>
            <span className="pc-dbv-run-pct">{pct}%</span>
          </div>
          <div className="pc-dbv-track" role="progressbar" aria-label="Progresso da transição" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <div className="pc-dbv-fill" style={{ width: `${pct}%` }} />
          </div>

          <div className="pc-dbv-cut-toolbar">
            <label className="pc-dbv-cut-search">
              <Search size={13} aria-hidden />
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrar por OS, cliente, técnico…" aria-label="Filtrar OS pendentes" />
            </label>
            <button type="button" className="pc-dbv-btn" onClick={exportCsv} disabled={!filtered.length}>
              <Download size={14} aria-hidden /> Exportar
            </button>
          </div>

          <div className="pc-dbv-cut-table-wrap">
            <table className="pc-dbv-table pc-dbv-cut-table">
              <thead>
                <tr>
                  <th>OS</th><th>Cliente</th><th>Tipo</th><th>Status</th><th>Técnico</th>
                  <th>Abertura</th><th className="is-num">Dias</th><th>Coluna</th><th aria-label="Atalhos" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.os}>
                    <td className="is-mono">{i.os}</td>
                    <td>{i.customer}</td>
                    <td>{i.type}</td>
                    <td>{i.status}</td>
                    <td>{i.technician}</td>
                    <td>{formatDate(i.openedAt)}</td>
                    <td className="is-num">{i.daysOpen ?? '—'}</td>
                    <td>{STAGE_LABELS[i.stage] || '—'}</td>
                    <td className="pc-dbv-cut-links">
                      <button type="button" onClick={() => openOnBoard(i.os)} title="Abrir detalhes no board">
                        Board <ArrowUpRight size={12} aria-hidden />
                      </button>
                      <button type="button" onClick={() => openClosing(i.os)} title="Ir para o fechamento">
                        Fechamento <ArrowUpRight size={12} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
                {!filtered.length ? (
                  <tr><td colSpan={9} className="pc-dbv-cut-empty">Nenhuma OS encontrada.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {phase === 'transition' || phase === 'drained' ? (
        <>
          <p className="pc-dbv-cut-note">
            <Info size={13} aria-hidden />
            Os relatórios de antes do cutover continuam lendo o SQL Server (somente leitura). Isso é esperado e não conta como pendência.
          </p>
          <p className="pc-dbv-cut-note">
            <Info size={13} aria-hidden />
            {status.cutover?.has_backup
              ? `Cópia de segurança da ativação gerada em ${formatDateTime(status.cutover.backup_confirmed_at)} (banco Vervify e tabelas do Pilot no SQL Server).`
              : 'Ativação sem cópia de segurança (ativada pelo script de teste).'}
          </p>
        </>
      ) : null}
    </div>
  );
}
